// A LEAD'S CONTACT HISTORY — one row per conversation, newest first, never overwritten.
//
// This is the thing a single `notes` field could not be. "What did we agree with them?"
// is a question about a sequence: he asked for a quote in August, went quiet, called back
// after the chag. A field you edit keeps only the last sentence anyone typed and quietly
// destroys the rest, and on a board two partners share it also loses who said what.
//
// SO THERE ARE THREE DIFFERENT THINGS ON A LEAD AND THIS IS ONLY ONE OF THEM:
//   the follow-up DATE     when to get back to them   — one value, on the lead form
//   the GENERAL info       who they are, what they want — one field, on the lead form
//   the CONTACT LOG        what was said, and when      — this, accumulating
//
// AND IT IS NOT A TASK LIST. The lead card shows both, one above the other, and they are
// deliberately different: a task is something to produce and can be marked done; a
// logged conversation already happened and has nothing to complete.
//
// Lazy: the log is fetched when the card is expanded, not with the board. Fifty leads
// would otherwise each drag their history into a screen that shows none of it folded.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import { clientNotesApi } from '../../api';
import he from '../../i18n/he';
import { todayStr } from '../../utils/dates';
import Button from '../../components/Button';
import DateText from '../../components/DateText';
import { DateInput, Textarea } from '../../components/Form';
import AsyncBoundary from '../../components/Loader';
import { messageForError } from '../../i18n/errors';

// One logged conversation. Read-only on the face of it: correcting a note is an edit of
// the record of what was said, which is rare and deliberate, so it is not offered inline
// beside every row — delete is, for the note written against the wrong lead.
function NoteRow({ note, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try { await clientNotesApi.remove(note.id); onDeleted(); }
    catch (e) { setError(messageForError(e)); setBusy(false); }
  };

  return (
    <div className="note-row">
      <div className="note-head">
        {/* The date of the CONVERSATION, not of the typing. See the server's
            resolveContactedOn: a Tuesday call written up on Friday is Tuesday's. */}
        <span className="note-date"><DateText value={note.contacted_on} /></span>
        {note.created_by_name && <span className="muted small-note">{note.created_by_name}</span>}
        <button
          type="button"
          className="note-remove"
          aria-label={he.common.delete}
          disabled={busy}
          onClick={remove}
        >
          ×
        </button>
      </div>
      {/* Opaque — displayed, never parsed. `pre-wrap` so the line breaks someone typed
          survive; this is a record of words, and reflowing it changes what it looks like
          they wrote. */}
      <p className="note-body">{note.body}</p>
      {error && <p className="error-text small-note">{error}</p>}
    </div>
  );
}

// The add form, inline rather than a modal. Logging a call is the most frequent thing
// that happens on this screen, and a dialog for two fields would put a click between
// hanging up and writing it down.
function AddNote({ clientId, onSaved, onCancel }) {
  const [contactedOn, setContactedOn] = useState(todayStr());
  const [body, setBody] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await clientNotesApi.create({ client_id: clientId, contacted_on: contactedOn, body });
      onSaved();
    } catch (e) { setError(messageForError(e)); setSaving(false); }
  };

  return (
    <div className="note-add">
      <Textarea
        label={he.leads.contactBody}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={he.leads.contactBodyPlaceholder}
      />
      {/* Defaults to today because most notes are written straight after the call, and
          stays editable because some are not. */}
      <DateInput
        label={he.leads.contactDate}
        value={contactedOn}
        onChange={(e) => setContactedOn(e.target.value)}
      />
      <div className="row">
        <Button small onClick={save} disabled={saving || !body.trim()}>{he.leads.contactSave}</Button>
        <Button small variant="subtle" onClick={onCancel}>{he.common.cancel}</Button>
      </div>
      {error && <p className="error-text small-note">{error}</p>}
    </div>
  );
}

export default function LeadNotes({ clientId }) {
  const { data, loading, error, reload } = useAsync(() => clientNotesApi.byClient(clientId), [clientId]);
  const [adding, setAdding] = useState(false);
  const notes = data || [];

  return (
    <div className="lead-notes">
      <div className="lead-notes-head">
        <span className="lead-notes-title">
          {he.leads.contactLog}
          {notes.length > 0 && <span className="count">{notes.length}</span>}
        </span>
        {!adding && (
          <Button small variant="ghost" onClick={() => setAdding(true)}>{he.leads.contactLogAdd}</Button>
        )}
      </div>

      {adding && (
        <AddNote
          clientId={clientId}
          onSaved={() => { setAdding(false); reload(); }}
          onCancel={() => setAdding(false)}
        />
      )}

      <AsyncBoundary loading={loading} error={error}>
        {notes.length === 0
          ? <p className="muted small-note">{he.leads.contactLogEmpty}</p>
          : notes.map((n) => <NoteRow key={n.id} note={n} onDeleted={reload} />)}
      </AsyncBoundary>
    </div>
  );
}
